import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Pencil } from "lucide-react";

import { API_SQE_SHIFT_TIMING } from "../../../../config/Endpoints.js";
import { getApi, putApi } from "../../../utils/api.ts";
import { accessToken } from "../../../utils/auth.ts";
import { getApiErrorMessage } from "../../../utils/api-error-message.ts";
import { showSuccess } from "../../../utils/toast.ts";
import { TimeField } from "../../ui/time-field/time-field.tsx";
import { TextField } from "../../ui/text-field/text-field.tsx";
import styles from "./sqe-shift-timing.module.css";

type ShiftTiming = {
  timing: string;
  totalShiftHours: string | number | null;
};

type ShiftResponse = { data?: ShiftTiming | null } | ShiftTiming;

function parseStartTime(value: string): number | null {
  const match = /^(1[0-2]|0?[1-9]):([0-5]\d)\s*(AM|PM)$/i.exec(value.trim());
  if (!match) return null;
  return (Number(match[1]) % 12 + (match[3].toUpperCase() === "PM" ? 12 : 0)) * 60 + Number(match[2]);
}

function formatTime(totalMinutes: number): string {
  const minutesInDay = ((totalMinutes % 1440) + 1440) % 1440;
  const hour = Math.floor(minutesInDay / 60);
  const minute = minutesInDay % 60;
  return `${hour % 12 || 12}:${String(minute).padStart(2, "0")} ${hour < 12 ? "AM" : "PM"}`;
}

function formatTimeInput(totalMinutes: number): string {
  const minutesInDay = ((totalMinutes % 1440) + 1440) % 1440;
  return `${String(Math.floor(minutesInDay / 60)).padStart(2, "0")}:${String(minutesInDay % 60).padStart(2, "0")}`;
}

function parseTimeInput(value: string): number | null {
  const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(value);
  return match ? Number(match[1]) * 60 + Number(match[2]) : null;
}

function timeInputFromApi(value?: string): string {
  const minutes = value ? parseStartTime(value) : null;
  return minutes == null ? "" : formatTimeInput(minutes);
}

function parseDuration(value: string | number | null, numericUnit: "hours" | "minutes" = "hours"): number | null {
  const trimmed = String(value ?? "").trim();
  if (/^\d+(?:\.\d+)?$/.test(trimmed)) {
    const number = Number(trimmed);
    return numericUnit === "minutes" ? number : Math.round(number * 60);
  }
  const match = /^(\d+)\s*(?:h|hr|hrs|hour|hours)(?:\s*(\d+)\s*(?:m|min|mins|minute|minutes))?$/i.exec(trimmed);
  if (!match) return null;
  if (Number(match[2] ?? 0) >= 60) return null;
  return Number(match[1]) * 60 + Number(match[2] ?? 0);
}

function displayDuration(value: string | number | null): string {
  const minutes = parseDuration(value, "minutes");
  if (minutes == null) return value == null ? "-" : String(value);
  const hours = Math.floor(minutes / 60);
  const remaining = minutes % 60;
  return `${hours} hr${remaining ? ` ${remaining} mins` : ""}`;
}

function durationHoursInput(value: string | number | null): string {
  const minutes = parseDuration(value, "minutes");
  return minutes == null ? "" : String(Number((minutes / 60).toFixed(2)));
}

export function SqeShiftTiming({ employeeId, readOnly = false, readEndpoint }: { employeeId: string | number; readOnly?: boolean; readEndpoint?: string }) {
  const [shift, setShift] = useState<ShiftTiming | null>(null);
  const [startTime, setStartTime] = useState("");
  const [duration, setDuration] = useState("");
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const loadShift = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    setError("");
    try {
      const response = await getApi<ShiftResponse>(readEndpoint ?? API_SQE_SHIFT_TIMING(employeeId), accessToken(), "json", signal);
      if (signal?.aborted) return;
      const body = response.data;
      const next = body && "data" in body ? body.data ?? null : body ?? null;
      setShift(next);
      setStartTime(timeInputFromApi(next?.timing));
      setDuration(next ? durationHoursInput(next.totalShiftHours) : "");
    } catch (cause) {
      if (signal?.aborted) return;
      if (cause && typeof cause === "object" && "status" in cause && cause.status === 404) {
        setShift(null);
        setStartTime("");
        setDuration("");
      } else {
        setError(getApiErrorMessage(cause, "Unable to load shift timing."));
      }
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [employeeId, readEndpoint]);

  useEffect(() => {
    const controller = new AbortController();
    void loadShift(controller.signal);
    return () => controller.abort();
  }, [loadShift]);

  const startMinutes = parseTimeInput(startTime);
  const durationMinutes = parseDuration(duration);
  const endTime = startMinutes != null && durationMinutes != null && durationMinutes > 0 && durationMinutes <= 1440
    ? formatTimeInput(startMinutes + durationMinutes)
    : "";
  const savedStartMinutes = shift?.timing ? parseStartTime(shift.timing) : null;
  const savedDurationMinutes = shift ? parseDuration(shift.totalShiftHours, "minutes") : null;
  const savedEndTime = savedStartMinutes != null && savedDurationMinutes != null && savedDurationMinutes > 0 && savedDurationMinutes <= 1440
    ? formatTime(savedStartMinutes + savedDurationMinutes)
    : "-";

  async function saveShift(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!/^\d+$/.test(duration)) {
      setError("Enter total shift hours as a whole number.");
      return;
    }
    if (startMinutes == null) {
      setError("Select a valid shift start time.");
      return;
    }
    if (durationMinutes == null || durationMinutes <= 0 || durationMinutes > 1440) {
      setError("Enter total shift hours between 0 and 24 hours.");
      return;
    }

    setSaving(true);
    setError("");
    try {
      const savedShift = {
        timing: formatTime(startMinutes),
        totalShiftHours: String(durationMinutes),
      };
      await putApi(API_SQE_SHIFT_TIMING(employeeId), accessToken(), savedShift);
      setShift(savedShift);
      setStartTime(formatTimeInput(startMinutes));
      setDuration(durationHoursInput(savedShift.totalShiftHours));
      setEditing(false);
      showSuccess("Shift timing saved successfully.");
      await loadShift();
    } catch (cause) {
      setError(getApiErrorMessage(cause, "Unable to save shift timing."));
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className={styles.section} aria-label="SQE shift timing">
      <div className={styles.heading}>
        <h3>SQE Shift Timing</h3>
        {!readOnly && !editing && !loading && (
          <button type="button" className={styles.editButton} aria-label="Edit SQE shift timing" title="Edit shift timing" onClick={() => setEditing(true)}><Pencil size={18} aria-hidden="true" /></button>
        )}
      </div>
      {loading ? <p className={styles.message}>Loading shift timing...</p> : editing ? (
        <form onSubmit={saveShift}>
          <div className={styles.fields}>
            <TextField id="sqe-total-shift-hours" label="Total Shift Hours" type="text" inputMode="numeric" pattern="[0-9]+" value={duration} onChange={(event) => setDuration(event.target.value.replace(/\D/g, ""))} placeholder="e.g. 9" required />
            <TimeField label="Shift Start Timing" value={startTime} onChange={setStartTime} required />
            <TimeField label="Shift End Timing" value={endTime} readOnly />
          </div>
          {error && <p className={styles.error} role="alert">{error}</p>}
          <div className={styles.actions}>
            <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>{saving ? "Saving..." : "Save Shift Timing"}</button>
            <button type="button" className="btn btn-secondary btn-sm" disabled={saving} onClick={() => { setEditing(false); setError(""); setStartTime(timeInputFromApi(shift?.timing)); setDuration(shift ? durationHoursInput(shift.totalShiftHours) : ""); }}>Cancel</button>
          </div>
        </form>
      ) : (
        <>
          {error && <p className={styles.error} role="alert">{error} <button type="button" onClick={() => void loadShift()}>Retry</button></p>}
          <div className={`${styles.fields} ${styles.viewFields}`}>
            <div><span>Total Shift Hours</span><strong>{shift ? displayDuration(shift.totalShiftHours) : "-"}</strong></div>
            <div><span>Shift Start Timing</span><strong>{shift?.timing || "-"}</strong></div>
            <div><span>Shift End Timing</span><strong>{savedEndTime}</strong></div>
          </div>
        </>
      )}
    </section>
  );
}
